/**
 * Parses dropdown options from a string.
 * Handles both JSON arrays and comma-separated strings.
 *
 * @param optionsString - The string containing dropdown options (JSON array or comma-separated)
 * @returns Array of option objects with label and value properties
 */
export const parseDropdownOptions = (
  optionsString: string | null | undefined
): { label: string; value: string }[] => {
  if (!optionsString) return [];

  const normalized = optionsString.trim();
  if (!normalized) return [];

  // Some backends serialize null/undefined as the literal string "null"/"undefined"
  const lowered = normalized.toLowerCase();
  if (lowered === "null" || lowered === "undefined") {
    return [];
  }

  // Try parsing as JSON first
  try {
    const parsed = JSON.parse(normalized);
    if (parsed === null || parsed === undefined) {
      return [];
    }
    if (Array.isArray(parsed)) {
      return parsed.map((option) => {
        if (typeof option === "string") {
          const opt = option.trim();
          const optLowered = opt.toLowerCase();
          if (!opt || optLowered === "null" || optLowered === "undefined") {
            return null;
          }
          return { label: option, value: option };
        }
        return {
          label: option.label || option.value || String(option),
          value: option.value || String(option),
        };
      }).filter((opt): opt is { label: string; value: string } => opt !== null);
    }
  } catch {
    // Not JSON, try comma-separated
  }

  // Fallback to comma-separated string
  return normalized
    .split(",")
    .map((opt) => opt.trim())
    .filter((opt) => {
      if (!opt) return false;
      const optLowered = opt.toLowerCase();
      return optLowered !== "null" && optLowered !== "undefined";
    })
    .map((opt) => ({ label: opt, value: opt }));
};
